import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, StarIcon } from '@heroicons/react/24/solid';
import aedIcon from '../assets/aed.svg';
import { getDiscountedPrice, PACKAGE_PRICING, formatAED } from '../config/pricing';

const photoConfig = PACKAGE_PRICING['Basic Photo Package – 1 Hour'];
const videoConfig = PACKAGE_PRICING['Basic Video Package – 1 Hour'];
const comboConfig = PACKAGE_PRICING['Combo Package (Photo + Video)'];

const Services: React.FC = () => {
  const services = [
    {
      id: 1,
      title: 'Basic Photo Package – 1 Hour',
      price: getDiscountedPrice('Basic Photo Package – 1 Hour'),
      originalPrice: photoConfig.basePrice ? formatAED(photoConfig.basePrice) : formatAED(500),
      discountPercent: photoConfig.discountPercent,
      popular: false,
      includes: [
        '1-hour on-site photography session',
        'All RAW photos delivered',
        '10 high-end retouched photos'
      ],
      suitableFor: [
        'Couples & portrait sessions',
        'Professional headshots',
        'Personal branding & social media content',
        'Small product shoots'
      ],
      additionalInfo: 'Additional hour: +AED 400 | Extra edited photo: +AED 25',
      description: 'A focused 1-hour photography session ideal for portraits, couples, headshots, and small product shoots. Includes RAW files and 10 premium edited images.',
    },
    {
      id: 2,
      title: 'Basic Video Package – 1 Hour',
      price: getDiscountedPrice('Basic Video Package – 1 Hour'),
      originalPrice: videoConfig.basePrice ? formatAED(videoConfig.basePrice) : formatAED(750),
      discountPercent: videoConfig.discountPercent,
      popular: false,
      includes: [
        '1-hour professional video shoot on-site',
        'One fully edited video (typically 1–3 minutes)',
        'Color correction, basic sound enhancement & clean cuts',
        'RAW footage available upon request'
      ],
      suitableFor: [
        'Cinematic videos for couples, brands or events',
        'Real estate walkthroughs and property introductions',
        'Commercial / promotional videos for products or services',
        'Podcast, talking-head or message-style recordings'
      ],
      additionalInfo:
        'Includes one edited video only (per 1-hour session). In simple cases, two short cuts may be delivered at the filmmaker’s discretion. ' +
        'Heavy motion graphics or advanced compositing are quoted as separate services.',
      description:
        'A focused 1-hour video package designed for cinematic, real estate, commercial or podcast-style content. ' +
        'You choose the concept, and we deliver a polished final video with professional editing and storytelling.',
    },
    {
      id: 3,
      title: 'Combo Package (Photo + Video)',
      price: getDiscountedPrice('Combo Package (Photo + Video)'),
      originalPrice: comboConfig.basePrice ? formatAED(comboConfig.basePrice) : formatAED(1050),
      discountPercent: comboConfig.discountPercent,
      popular: true,
      includes: [
        '1-hour combined photo & video shoot',
        'All RAW photos delivered',
        '10 high-end retouched photos',
        'One fully edited video (typically 1–3 minutes)',
        'Color correction, basic sound enhancement & clean cuts',
        'RAW footage available upon request'
      ],
      suitableFor: [
        'Engagements & intimate events',
        'Personal branding sessions',
        'Product photography + promo video',
        'Small businesses needing both photos & video'
      ],
      additionalInfo: 'Each additional hour (Photo + Video): +AED 800 | Extra edited photo: +AED 25 | Extra video: +AED 300',
      description: 'A complete media solution providing both photography and videography in a single session. Perfect for clients who want full coverage while saving compared to booking separately.',
    },
    {
      id: 4,
      title: 'Custom Package',
      price: 'Quote',
      popular: false,
      includes: [
        'Flexible coverage (2–10+ hours)',
        'Photography & videography',
        'Advanced editing: cinematic color grading & multi-angle cuts',
        'Optional add-ons: drone shots, live social media coverage, multi-day shoots',
        'Pre-event consultation & planning'
      ],
      suitableFor: [
        'Weddings & large events',
        'Corporate conferences & product launches',
        'Brand campaigns & commercial productions',
        'Special projects requiring customized solutions'
      ],
      additionalInfo: 'Pricing set after consultation based on duration, team size, equipment load, and editing complexity.',
      description: 'A fully customizable package designed for weddings, corporate events, and large-scale productions. Built to match your exact creative and coverage requirements.',
    }
  ];
  

  const scrollToSection = (sectionId: string) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-20 bg-white logo-watermark logo-watermark-light">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Photography Services
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Professional photography packages tailored to capture your most important moments
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className={`relative bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-2xl ${
                service.popular 
                  ? 'border-black scale-105' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Popular Badge */}
              {service.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-black text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                    <StarIcon className="h-4 w-4" />
                    <span>Most Popular</span>
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Service Header */}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {service.title}
                  </h3>
                  <div className="mb-2 flex flex-col items-center justify-center">
                    {service.price === 'Quote' ? (
                      <span className="text-2xl font-semibold text-gray-900">Quote</span>
                    ) : (
                      <>
                        <div className="text-4xl font-bold text-black flex items-center justify-center">
                          <img src={aedIcon} alt="AED" className="w-8 h-8 mr-2" />
                          {service.price}
                        </div>
                        {service.originalPrice && service.discountPercent ? (
                          <p className="mt-1 text-xs text-gray-500">
                            <span className="line-through mr-2">
                              AED {service.originalPrice}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-semibold uppercase tracking-wide">
                              {service.discountPercent}% off
                            </span>
                          </p>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>

                {/* Includes Section */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">Includes:</h4>
                  <div className="space-y-2">
                    {service.includes.map((item, itemIndex) => (
                      <motion.div
                        key={itemIndex}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: (index * 0.1) + (itemIndex * 0.05) }}
                        viewport={{ once: true }}
                        className="flex items-start space-x-3"
                      >
                        <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Suitable For Section */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">Suitable for:</h4>
                  <div className="space-y-2">
                    {service.suitableFor.map((item, itemIndex) => (
                      <motion.div
                        key={itemIndex}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: (index * 0.1) + (service.includes.length * 0.05) + (itemIndex * 0.05) }}
                        viewport={{ once: true }}
                        className="flex items-start space-x-3"
                      >
                        <CheckIcon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Additional Info */}
                {service.additionalInfo && (
                  <div className="mb-6 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-600 italic">{service.additionalInfo}</p>
                  </div>
                )}

                {/* CTA Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => scrollToSection('#booking')}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                    service.popular
                      ? 'bg-black text-white hover:bg-gray-800'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  Book This Package
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="text-gray-600 mb-6">
            Need a custom package? We're happy to create something special for your needs.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection('#contact')}
            className="border-2 border-black text-black px-8 py-3 rounded-full font-semibold hover:bg-black hover:text-white transition-all duration-200"
          >
            Contact Us for Custom Quote
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
